import React from "react";
import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import {
  Box,
  BreadcrumbItem,
  Button,
  Icon,
  Separator,
  Tabs,
  TabsList,
  TabsOverflow,
  TabsStripSkeleton,
  TabsOverflowSpacer,
  TabsOverflowViewport,
  Text,
  useIsMobile,
  ViewTabsRailSeparator,
} from "@salestrak/ui";

import { PageHeaderBreadcrumb } from "#app/components/page-header";

import { AccessDenied } from "#app/components/permissions/require-permission";
import { WorkflowBreadcrumbMenu } from "#app/components/workflows/workflow-breadcrumb-menu";
import { WorkflowShareDialog } from "#app/components/workflows/workflow-share-dialog";
import { WorkflowAutomationsPopover } from "#app/components/automations/workflow-automations-popover";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import {
  useAppData,
  type EntityViewRow,
  type WorkspaceWorkflow as Workflow,
} from "#app/lib/data";
import { canManageSharing } from "#app/lib/utils/access-level";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import { IssueFilterProvider } from "#app/lib/filters/issue-filter-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkflowId } from "#app/lib/hooks/use-workflow-id";
import { useEntityViewTabOrder } from "#app/lib/hooks/use-view-tab-order";
import { useRememberLastView } from "#app/lib/views/last-view";
import { WORKFLOWS_MODULE_SCOPE } from "#app/lib/hooks/use-workflows-landing";
import { ViewsProvider, useViews } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";
import {
  AddViewMenu,
  ViewOverflowMenu,
  ViewTab,
} from "#app/components/view-options";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  AnalyticsScope,
  ViewEvent,
} from "#app/lib/analytics";
import { EntityShellSkeleton } from "#app/components/loading/entity-shell-skeleton";

/**
 * Single-workflow chrome wrapping the view routes: the breadcrumb masthead,
 * Share action, view tabs, object-level access guard, and the shared filter
 * context + panel overlays. The header renders here once, above `<Outlet />`,
 * so it is identical across all views. Each route renders its own body and
 * its own toolbar band below the tab strip.
 *
 * The tabs ARE the workflow's saved views — data, not a constant. The server
 * seeds the built-ins (List / Board / Dashboard, which keep their own URL
 * segments), and anyone with `create.views` adds more beside them.
 *
 * Every viewer sees every tab. `create.views` decides who may WRITE a view,
 * never who may see one: a permission that changed which tabs exist would
 * hand two people looking at the same workflow two different workflows. The
 * rail's own actions (`+ View`, and the hairline that introduces them) are the
 * one thing the permission removes — an action the viewer cannot take is not
 * shown.
 *
 * Two things on the strip are the VIEWER'S, not the row's: the drag order,
 * and the pins that hold a run of tabs at the front of it. Both live in the
 * user settings bag and ride no permission.
 */

/** The last path segment: a built-in key (`list`) or a saved view's id. */
function useActiveViewKey(): string | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return React.useMemo(() => pathname.split("/").pop() ?? null, [pathname]);
}

function WorkflowLayout() {
  const workflowId = useWorkflowId();
  const activeKey = useActiveViewKey();
  const scope = React.useMemo(
    () => ({ kind: "workflow" as const, id: workflowId ?? "" }),
    [workflowId],
  );
  if (!workflowId) return null;
  return (
    <ViewsProvider key={workflowId} scope={scope} activeKey={activeKey}>
      <WorkflowFilterScope>
        <WorkflowLayoutInner />
      </WorkflowFilterScope>
    </ViewsProvider>
  );
}

/**
 * Mounts the screen's filter set, keyed per view: inside this views scope the
 * provider binds to the active view's config draft, so each tab starts from
 * its own saved filters and a tab switch re-seeds from the next view's.
 */
function WorkflowFilterScope({ children }: { children: React.ReactNode }) {
  const { activeView } = useViews();
  return (
    <IssueFilterProvider key={activeView?.id ?? ""} analyticsScope={AnalyticsScope.Workflow}>
      {children}
    </IssueFilterProvider>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/workflow/$workflowId",
)({ component: WorkflowLayout });

function WorkflowLayoutInner() {
  const workflowId = useWorkflowId();
  const { replace } = useAppNavigate();
  const isMobile = useIsMobile();
  const { track } = useAnalytics();
  const { workflows, currentUser, isLoading } = useAppData();

  const workflow = React.useMemo(
    () => workflows.find((wf: Workflow) => wf.workflowId === workflowId),
    [workflows, workflowId],
  );

  const automationsEnabled = useAutomationsEnabled();
  const [shareOpen, setShareOpen] = React.useState(false);
  const { scope, views, activeView, pinnedIds, canManage, initialLoading, trackView } =
    useViews();

  // The bare workflow URL reopens this view next time. Only a view the row
  // holds is worth remembering: the index route (no view) and a deleted
  // view's URL both leave `activeView` null and write nothing.
  useRememberLastView(
    scope,
    activeView ? (activeView.builtInKey ?? activeView.id) : null,
  );
  // The Workflows module reopens this workflow next time (its tile, `G W`,
  // "Go to Workflows"). Only a workflow the viewer can reach is worth
  // remembering — an id outside the visibility-scoped set is a denied or
  // deleted row, and nothing is written for it.
  useRememberLastView(WORKFLOWS_MODULE_SCOPE, workflow ? workflow.workflowId : null);

  const viewsById = React.useMemo(
    () => new Map(views.map((view) => [view.id, view])),
    [views],
  );
  // The server's `position` order is the canonical sequence; the hook layers
  // this person's drag order over it, with the pinned run hoisted ahead.
  const viewIds = React.useMemo(() => views.map((view) => view.id), [views]);
  const { order: tabOrder, reorderVisible } = useEntityViewTabOrder(
    `workflow:${workflowId}`,
    viewIds,
    pinnedIds,
  );

  // A drag in the strip or the "{n} more" list; a promote from the menu
  // reorders too, but it is a pick, not a drag.
  const dragReorder = React.useCallback(
    (via: "strip" | "more_menu") => (order: string[]) => {
      reorderVisible(order);
      trackView(ViewEvent.Reorder, undefined, { via });
    },
    [reorderVisible, trackView],
  );

  // The saved order can outlive the row that fed it, so an id the row no
  // longer holds drops out of the strip.
  const visibleTabs = React.useMemo(
    () => tabOrder.filter((id) => viewsById.has(id)),
    [tabOrder, viewsById],
  );

  // Pinned tabs hold the front of the strip as a run of their own: the list
  // takes TWO runs, so a pinned view drags among the pinned and the rest drag
  // behind the hairline, and neither crosses it. The hook's reorder splices
  // the whole sequence back around the hidden tabs.
  const pinnedSet = React.useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const pinnedTabs = React.useMemo(
    () => visibleTabs.filter((id) => pinnedSet.has(id)),
    [visibleTabs, pinnedSet],
  );
  const restTabs = React.useMemo(
    () => visibleTabs.filter((id) => !pinnedSet.has(id)),
    [visibleTabs, pinnedSet],
  );
  const tabRuns = React.useMemo(
    () => [pinnedTabs, restTabs],
    [pinnedTabs, restTabs],
  );
  const pinnedCount = pinnedTabs.length;

  const openView = React.useCallback(
    (id: string) => {
      const view = viewsById.get(id);
      if (!view || id === activeView?.id) return;
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.SwitchTab,
        // A saved view's name is free text; the TYPE is the bounded dimension.
        data: { scope: AnalyticsScope.Workflow, tab: view.builtInKey ?? view.type },
      });
      replace(viewPath(scope, view));
    },
    [viewsById, activeView, scope, replace, track],
  );

  // A view picked from the "{n} more" menu keeps its tab: it slots into the
  // last visible position OF ITS RUN — written through the same personal drag
  // order the strip already persists — instead of folding straight back on
  // the next measure. The fold is trailing, so a folded pinned view means the
  // rest is folded whole and the pinned run is the one with a visible slot.
  const promoteView = React.useCallback(
    (view: EntityViewRow, hidden: readonly string[]) => {
      openView(view.id);
      const hiddenSet = new Set(hidden);
      const isPinned = pinnedSet.has(view.id);
      const run = isPinned ? pinnedTabs : restTabs;
      const others = run.filter((id) => id !== view.id);
      const visibleInRun = run.filter((id) => !hiddenSet.has(id)).length;
      const at = Math.max(0, visibleInRun - 1);
      const nextRun = [...others.slice(0, at), view.id, ...others.slice(at)];
      reorderVisible(
        isPinned ? [...nextRun, ...restTabs] : [...pinnedTabs, ...nextRun],
      );
    },
    [openView, pinnedSet, pinnedTabs, restTabs, reorderVisible],
  );

  // Object-level guard: the API filters the `workflows` list by visibility,
  // so a workflow missing from it after load means the viewer can't see it
  // (or it doesn't exist). Same loading rule as RequirePermission — wait for
  // `!isLoading` with a present `currentUser` so a workspace switch doesn't
  // evaluate against the previous workspace's (stale) workflow list.
  if (isLoading || !currentUser) {
    return <EntityShellSkeleton />;
  }

  if (!workflow) {
    return <AccessDenied />;
  }

  // Only `full_edit` viewers can manage the workflow's share list — the
  // header's Share button and the tab menu's footer read the same gate.
  const canShare = canManageSharing(workflow.viewerAccessLevel);

  return (
    <Box fill="flex" direction="column" minHeight={0} minWidth={0}>
      {/* Same masthead structure and rhythm as the team shell: 8px top, 28px
        * first line, 8px gap, then the 40px underline tab strip — 84px to the
        * separator its active bar rides. The two entity shells must stay
        * visually parallel. */}
      <Box gap="sm" paddingTop="sm" paddingX="md" shrink={false}>
        {/* First line pinned to the shell-wide 28px contract (see PageHeader)
          * so the breadcrumb holds its line even when the trailing actions
          * are hidden. */}
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          minHeight={28}
        >
          <PageHeaderBreadcrumb>
            <BreadcrumbItem>
              {/* This crumb IS the current page, so a press has nowhere to
                  route — instead it drops the workflow's management menu
                  (rename, appearance, stages, automations, archive, delete,
                  ...), announced by the crumb's chevron. */}
              <WorkflowBreadcrumbMenu
                workflow={workflow}
                canShare={canShare}
                onShare={() => setShareOpen(true)}
              />
            </BreadcrumbItem>
          </PageHeaderBreadcrumb>

          {/* Automations and Share are one action cluster: a 1px seam keeps
              the ghost hover fills reading as adjacent tiles, not two islands
              with a gutter between them. */}
          <Box direction="row" align="center" gap={1}>
            {/* Automations for this workflow — a popover to create, manage,
                and toggle the automations scoped here. When automations are
                active, the trigger's zap goes amber with a count. */}
            {automationsEnabled && workflowId ? (
              <WorkflowAutomationsPopover workflowId={workflowId} />
            ) : null}

            {canShare ? (
              <Button
                variant="ghost"
                // Match the adjacent masthead controls at 28px.
                size="sm"
                shape="pill"
                onClick={() => setShareOpen(true)}
                aria-label="Share workflow"
              >
                <Icon name="users" size="sm" color="muted" />
                <Text variant="muted">Share</Text>
              </Button>
            ) : null}
          </Box>
        </Box>

        {/* The spacer at the row's end is what measures the strip's spare
            width, so the row spans the full masthead. */}
        <Box direction="row" align="center" gap="xs" minWidth={0}>
          {initialLoading && views.length === 0 ? (
            // The row is in flight and nothing is cached: three tab stand-ins
            // hold the strip's height and rhythm so the rail's own actions
            // keep their place and the tabs land where the bars were. Delayed,
            // so a cache-warm revisit paints the real tabs and never a bar.
            <>
              <TabsStripSkeleton delayed />
              {canManage && !isMobile ? (
                <>
                  <ViewTabsRailSeparator />
                  <AddViewMenu
                    onCreated={(view) => replace(viewPath(scope, view))}
                  />
                </>
              ) : null}
            </>
          ) : (
            <TabsOverflow order={visibleTabs}>
              {({ hidden }) => {
                const hiddenSet = new Set(hidden);
                // A phone's rail has no room for an add-view control beside
                // the tabs: `+ View` always lives in the roster's "New View"
                // row there, whether or not the views fit, so the roster
                // trigger always renders and the strip ends seamlessly at
                // it — no hairline, no second control.
                const foldsAddView = isMobile && canManage;
                return (
                  <>
                    {/* The strip yields width instead of painting over the
                        rail: views that stop fitting fold into the chevron
                        menu beside it rather than clipping at a scroll
                        edge. */}
                    <TabsOverflowViewport>
                      <Tabs
                        value={activeView?.id ?? ""}
                        onValueChange={openView}
                      >
                        <TabsList
                          onOrderChange={dragReorder("strip")}
                          order={tabRuns}
                          variant="underline"
                        >
                          {visibleTabs.map((id, index) => {
                            const view = viewsById.get(id);
                            if (!view) return null;
                            return (
                              <React.Fragment key={id}>
                                {/* The hairline closes the pinned run —
                                    strong, because it is the boundary a
                                    drag stops at, the same rung the detail
                                    dialogs draw after Summary. */}
                                {index === pinnedCount && pinnedCount > 0 ? (
                                  <ViewTabsRailSeparator tone="strong" />
                                ) : null}
                                <ViewTab
                                  overflowHidden={hiddenSet.has(id)}
                                  view={view}
                                />
                              </React.Fragment>
                            );
                          })}
                        </TabsList>
                      </Tabs>
                    </TabsOverflowViewport>
                    {hidden.length > 0 || foldsAddView ? (
                      <ViewOverflowMenu
                        activeId={activeView?.id}
                        hiddenCount={hidden.length}
                        pinnedIds={pinnedIds}
                        onReorder={dragReorder("more_menu")}
                        onSelect={(view) =>
                          hiddenSet.has(view.id)
                            ? promoteView(view, hidden)
                            : openView(view.id)
                        }
                        views={visibleTabs.flatMap(
                          (id) => viewsById.get(id) ?? [],
                        )}
                        onCreated={
                          foldsAddView
                            ? (view) => replace(viewPath(scope, view))
                            : undefined
                        }
                      />
                    ) : null}
                    {/* The hairline introduces the rail's actions; without
                        `create.views` there is no action to introduce, so
                        neither renders and the strip ends at its last tab
                        (or, on a phone, at the roster that folded it in). */}
                    {canManage && !foldsAddView ? (
                      <>
                        <ViewTabsRailSeparator />
                        <AddViewMenu
                          onCreated={(view) => replace(viewPath(scope, view))}
                        />
                      </>
                    ) : null}
                    <TabsOverflowSpacer />
                  </>
                );
              }}
            </TabsOverflow>
          )}
          </Box>
      </Box>

      {/* Flush under the strip — the active tab's underline bar overlaps this
          hairline, so any padding between them breaks the genre. */}
      <Separator />

      {/* Anchor for the filter drawer: it pins to this below-tabs region so
          the view tabs stay visible — the drawer filters the tabbed area. */}
      <Box
        id={VIEW_PANE_ANCHOR_ID}
        direction="column"
        fill="flex"
        minHeight={0}
        minWidth={0}
        overflow="hidden"
      >
        <Outlet />
      </Box>

      <WorkflowShareDialog
        workflowId={shareOpen ? (workflowId ?? null) : null}
        onOpenChange={(open) => {
          if (!open) setShareOpen(false);
        }}
      />
    </Box>
  );
}
